import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, Globe, MessageSquare, QrCode } from 'lucide-react';
import { PhoneInput } from '@/components/PhoneInput';
import { ContactQRDialog } from '@/components/ContactQRDialog';
import { trackEvent } from '@/lib/analytics';

const AddContact = () => {
  const [phone, setPhone] = useState('+1');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [senderName, setSenderName] = useState('');
  // When true, shows the invitation preview card instead of the search form
  const [showPreview, setShowPreview] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const isMobile = useIsMobile();

  // Step 1: check if the number belongs to an existing user
  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !user) return;

    setLoading(true);

    try {
      // Fetch the sender's display name upfront (needed for both flows)
      const { data: profile } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', user.id)
        .single();
      setSenderName(profile?.name || 'A friend');

      const { data: contactResult, error: contactError } = await supabase
        .rpc('get_user_by_phone', { phone_number: phone.trim() });

      if (contactError) throw contactError;

      const contactData =
        contactResult && contactResult.length > 0
          ? { id: contactResult[0].user_id, name: contactResult[0].user_name }
          : null;

      // ── Not registered yet: show the invitation preview ──
      if (!contactData) {
        setShowPreview(true);
        return;
      }

      // ── Can't chat with yourself ──
      if (contactData.id === user.id) {
        toast({
          title: t('error'),
          description: t('error'),
          variant: 'destructive',
        });
        return;
      }

      // ── Find or create a 1-on-1 chat via secure RPC ──
      const { data: chatId, error: rpcError } = await supabase.rpc(
        'find_or_create_one_on_one_chat',
        { _other_user_id: contactData.id }
      );
      if (rpcError) throw rpcError;

      toast({
        title: t('alreadyOnApp'),
        description: t('userFound'),
      });
      navigate(`/chat/${chatId}`);
    } catch (error: any) {
      console.error('Error checking contact:', error);
      toast({
        title: t('error'),
        description: error.message || t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: send the SMS invitation after the user has seen the preview
  const handleSendInvite = async () => {
    if (!user) return;
    setSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const { error: inviteError } = await supabase.functions.invoke(
        'send-sms-invite',
        {
          headers: session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : undefined,
          body: {
            phoneNumber: phone.trim(),
            inviterName: senderName,
          },
        }
      );

      if (inviteError) {
        // Supabase FunctionsHttpError stores the actual JSON body in error.context
        // (a Response object). JSON.parse(error.message) never works for HTTP errors.
        let errorMessage = t('tryAgain');
        try {
          if (inviteError.context && typeof inviteError.context.json === 'function') {
            const body = await inviteError.context.json();
            if (body?.error) errorMessage = body.error;
          } else if (inviteError.message) {
            errorMessage = inviteError.message;
          }
        } catch {
          if (inviteError.message) errorMessage = inviteError.message;
        }
        throw new Error(errorMessage);
      }

      trackEvent('invite_sent', { channel: 'sms' });

      toast({
        title: t('inviteSent'),
        description: t('inviteSent'),
      });

      // Reset back to the search form
      setShowPreview(false);
      setPhone('+1');
    } catch (error: any) {
      console.error('Error sending invite:', error);
      toast({
        title: t('error'),
        description: error.message || t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`${isMobile ? 'safe-area-screen' : 'flex-1'} flex flex-col bg-background overflow-hidden`}>
      <div className="flex-1 overflow-y-auto">
        <div className={`max-w-md mx-auto p-4 ${isMobile ? 'pt-[max(env(safe-area-inset-top),1rem)] pb-[max(env(safe-area-inset-bottom),1rem)]' : 'py-6'}`}>

          <div className="flex items-center gap-2 mb-6">
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (showPreview) {
                    setShowPreview(false);
                  } else {
                    navigate('/chats');
                  }
                }}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-2xl font-bold">
              {showPreview ? t('sendInvite') : t('invitePageTitle')}
            </h1>
          </div>

          {!showPreview ? (
            /* ── Step 1: phone number search ── */
            <Card className="p-6">
              <form onSubmit={handleCheck} className="space-y-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Globe className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('phoneNumberHint')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')}</Label>
                  <PhoneInput value={phone} onChange={setPhone} />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? t('searching') : t('startChatting')}
                </Button>

                <div className="relative flex items-center my-2">
                  <div className="flex-1 border-t border-border" />
                  <span className="px-3 text-xs text-muted-foreground uppercase">or</span>
                  <div className="flex-1 border-t border-border" />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setQrOpen(true)}
                >
                  <QrCode className="mr-2 h-4 w-4" />
                  Scan or share QR code
                </Button>
              </form>
            </Card>
          ) : (
            /* ── Step 2: invitation preview ── */
            <Card className="p-6 space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-3">
                  <MessageSquare className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="font-semibold text-base">{t('userNotFound')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('invitePreviewHeader')}
                </p>
              </div>

              {/* SMS preview bubble */}
              <div className="relative bg-muted/60 rounded-2xl rounded-tl-sm px-4 py-3 border text-sm leading-relaxed">
                <p className="text-foreground">
                  "{senderName} wants to chat with you on Global Chat Connect — you'll both understand each other perfectly."
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  + download link for iOS & Android
                </p>
              </div>

              <div className="space-y-2 pt-1">
                <Button
                  className="w-full"
                  onClick={handleSendInvite}
                  disabled={sending}
                >
                  {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {sending ? t('sending') : t('sendInvite')}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setShowPreview(false)}
                  disabled={sending}
                >
                  {t('back')}
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
      <ContactQRDialog open={qrOpen} onOpenChange={setQrOpen} />
    </div>
  );
};

export default AddContact;
